import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Copy, KeyRound, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Two-factor authentication toggle for the profile edit modal.
 *
 * Enabling adds a 6-digit email code step to every future login.
 * Requires the user's current password as a re-auth check so a stolen
 * session can't silently flip 2FA (per the auth playbook).
 */
export default function TwoFactorBlock({ enabled, onToggled }) {
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshCodes, setFreshCodes] = useState(null);
  const [copied, setCopied] = useState(false);
  const nextValue = !enabled;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/toggle", {
        enabled: nextValue,
        password,
      });
      onToggled?.(data.two_factor_enabled);
      setConfirming(false);
      setPassword("");
      if (data.two_factor_enabled && Array.isArray(data.backup_codes)) {
        setFreshCodes(data.backup_codes);
        toast.success("2FA on. Save your 8 backup codes below — this is your only chance.");
      } else {
        toast.success(data.two_factor_enabled
          ? "Two-factor sign-in is ON."
          : "Two-factor sign-in is OFF.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update 2FA.");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    const pw = window.prompt("Enter your current password to rotate backup codes:");
    if (!pw) return;
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { password: pw });
      setFreshCodes(data.backup_codes);
      toast.success("New backup codes generated — old ones are now invalid.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate codes.");
    }
  };

  const copyCodes = async () => {
    if (!freshCodes) return;
    try {
      await navigator.clipboard.writeText(freshCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Backup codes copied.");
    } catch { toast.error("Copy failed."); }
  };

  return (
    <div className="border-t border-white/5 pt-4 mt-2" data-testid="profile-edit-2fa-block">
      <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-2 flex items-center gap-2">
        {enabled ? <ShieldCheck size={12} /> : <ShieldOff size={12} />} Two-factor sign-in
      </div>
      {enabled ? (
        <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
          Every login sends a 6-digit code to your email before letting you in — great for creators and admins.
        </p>
      ) : (
        <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
          Add a second layer to your login. We'll email you a fresh 6-digit code every time you sign in, and give you 8 one-time backup codes for when you can't reach your email.
        </p>
      )}

      {freshCodes && (
        <div
          className="mb-4 border-l-2 border-amber-400 bg-amber-500/5 px-3 py-3"
          data-testid="profile-2fa-backup-codes-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 flex items-center gap-2">
              <KeyRound size={12} /> Save these 8 backup codes
            </div>
            <button
              onClick={copyCodes}
              className="text-xs text-amber-300 hover:text-white flex items-center gap-1"
              data-testid="profile-2fa-backup-copy"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy all"}
            </button>
          </div>
          <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
            Each code works ONCE if you can't access your email. Store them in a password manager now — we won't show them again.
          </p>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-sm">
            {freshCodes.map((c) => (
              <div key={c} className="bg-black/40 border border-white/10 px-3 py-1.5 text-amber-200 tracking-wider text-center select-all">
                {c}
              </div>
            ))}
          </div>
        </div>
      )}

      {enabled && !freshCodes && (
        <button
          type="button"
          onClick={regenerate}
          className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1.5 mb-3"
          data-testid="profile-2fa-regenerate-backup"
        >
          <KeyRound size={12} /> Regenerate backup codes
        </button>
      )}

      {!confirming ? (
        <Button
          type="button"
          onClick={() => setConfirming(true)}
          variant="outline"
          className={`rounded-none bg-transparent border text-white hover:bg-white/[0.03] flex items-center gap-2 ${
            enabled ? "border-white/15 hover:border-rose-500" : "border-emerald-500/50 hover:border-emerald-400 text-emerald-300"
          }`}
          data-testid="profile-2fa-toggle-button"
        >
          {enabled ? (<><ShieldOff size={14} /> Turn off 2FA</>) : (<><ShieldCheck size={14} /> Turn on 2FA</>)}
        </Button>
      ) : (
        <form onSubmit={submit} className="space-y-2" data-testid="profile-2fa-confirm-form">
          <div className="text-xs text-zinc-500">
            Enter your current password to {enabled ? "turn off" : "turn on"} two-factor sign-in.
          </div>
          <Input
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Current password"
            className="bg-black/40 border-white/10 rounded-none py-5"
            data-testid="profile-2fa-password-input"
          />
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              disabled={busy || !password}
              className="btn-crimson rounded-none"
              data-testid="profile-2fa-confirm-button"
            >
              {busy ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving…</> : (enabled ? "Turn off 2FA" : "Turn on 2FA")}
            </Button>
            <Button
              type="button"
              onClick={() => { setConfirming(false); setPassword(""); }}
              variant="ghost"
              className="rounded-none text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
